import { useState, useEffect, useCallback, useRef } from "react";
import { useRevalidateOnFocus } from "@/hooks/useRevalidateOnFocus";
import { observer } from "mobx-react-lite";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2,
  AlertCircle,
  RefreshCw,
  Link,
  Eye,
  Clock,
  XCircle,
  Copy,
  Check,
  FileText,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  listShares,
  revokeShare,
  isShareActive,
  getExpiryInfo,
  type Share,
} from "@/stores/documents/repository/shares";
import { buildShareUrl } from "@/utils/shareUrl";
import { formatDistanceToNow } from "date-fns";
import { et } from "date-fns/locale";
import { useLanguage } from "@/lib/i18n/LanguageContext";

interface ManageSharesDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog open state changes */
  onOpenChange: (open: boolean) => void;
  /** Building ID to list shares for */
  buildingId: string;
}

/**
 * Dialog for viewing and managing existing share links.
 * Shows list of shares with stats, allows revoking active shares.
 */
export const ManageSharesDialog = observer(function ManageSharesDialog({
  open,
  onOpenChange,
  buildingId,
}: ManageSharesDialogProps) {
  const { toast } = useToast();
  const { t } = useLanguage();
  const m = t.documents.manageSharesDialog;

  // Data state
  const [shares, setShares] = useState<Share[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Revoke confirmation state
  const [revokeTarget, setRevokeTarget] = useState<Share | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);

  // Copy state
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Only the newest load may write state. Two loads are routinely in flight at
  // once here — a focus revalidation can land on top of the open load, and a
  // load started for the previous building resolves long after the reset below
  // — and either would paint rows and share tokens this dialog is not showing.
  const loadGenerationRef = useRef(0);

  const loadShares = useCallback(async () => {
    if (!buildingId) return;

    const generation = ++loadGenerationRef.current;
    setIsLoading(true);
    setError(null);

    try {
      const data = await listShares(buildingId);
      if (generation !== loadGenerationRef.current) return;
      setShares(data);
    } catch (err) {
      if (generation !== loadGenerationRef.current) return;
      setError(err instanceof Error ? err.message : m.failedLoad);
    } finally {
      if (generation === loadGenerationRef.current) setIsLoading(false);
    }
  }, [buildingId, m]);

  // A different building's shares must not be what the next open renders while
  // its own load is in flight — `shares` outlives both `open` and `buildingId`,
  // and bumping the generation is what actually abandons that load rather than
  // merely clearing what it will overwrite. Declared BEFORE the effect that
  // loads: effects run in declaration order, so a mount (or a building change
  // with the dialog open) resets first and starts the load second, instead of
  // invalidating the load it just started.
  useEffect(() => {
    loadGenerationRef.current += 1;
    setShares([]);
    setError(null);
    // The abandoned load's `finally` is generation-guarded and will not lower
    // this, and `loadShares` returns early without raising it when there is no
    // building to load — so leaving it set strands the pane on its spinner.
    setIsLoading(false);
  }, [buildingId]);

  // Load shares when dialog opens
  useEffect(() => {
    if (open) {
      loadShares();
    }
  }, [open, loadShares]);

  // Share tables are not published for realtime, and this dialog can sit open
  // for a long time while view counts and expiry move underneath it.
  useRevalidateOnFocus(loadShares, { enabled: open });

  const handleRevoke = useCallback(async () => {
    if (!revokeTarget) return;

    setIsRevoking(true);

    try {
      await revokeShare(revokeTarget.id);

      // Abandon any load still in flight, exactly as a building change does: a
      // focus revalidation started before this revoke passes its own generation
      // check when it resolves and re-adds the share the user just revoked,
      // live Copy link and all. A local mutation of the list invalidates the
      // loads it predates, not just the loads that follow it.
      loadGenerationRef.current += 1;
      // The abandoned load's `finally` is generation-guarded and will not lower
      // this, so leaving it set strands the pane on its spinner.
      setIsLoading(false);

      // Remove from local state (revokeShare soft-deletes, and listShares filters revoked shares)
      setShares((prev) => prev.filter((s) => s.id !== revokeTarget.id));

      toast({
        title: m.revokedToastTitle,
        description: m.revokedToastDesc,
      });
    } catch (err) {
      toast({
        title: m.revokeFailedToastTitle,
        description: err instanceof Error ? err.message : m.revokeFailedToastDesc,
        variant: "destructive",
      });
    } finally {
      setIsRevoking(false);
      setRevokeTarget(null);
    }
  }, [revokeTarget, toast, m]);

  const handleCopyLink = useCallback(
    async (share: Share) => {
      const url = buildShareUrl(share.share_token);

      try {
        await navigator.clipboard.writeText(url);
        setCopiedId(share.id);
        setTimeout(() => setCopiedId(null), 2000);

        toast({
          title: m.linkCopiedToastTitle,
          description: m.linkCopiedToastDesc,
        });
      } catch {
        toast({
          title: m.copyFailedToastTitle,
          description: m.copyFailedToastDesc,
          variant: "destructive",
        });
      }
    },
    [toast, m]
  );

  const getStatusBadge = (share: Share) => {
    if (share.is_revoked) {
      return (
        <Badge variant="destructive" className="text-xs">
          {m.statusRevoked}
        </Badge>
      );
    }
    if (!isShareActive(share)) {
      return (
        <Badge variant="secondary" className="text-xs">
          {m.statusExpired}
        </Badge>
      );
    }
    return (
      <Badge variant="default" className="bg-green-600 text-xs">
        {m.statusActive}
      </Badge>
    );
  };

  const activeShares = shares.filter((s) => !s.is_revoked && isShareActive(s));
  const inactiveShares = shares.filter((s) => s.is_revoked || !isShareActive(s));

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link className="h-5 w-5" />
              {m.title}
            </DialogTitle>
            <DialogDescription>{m.description}</DialogDescription>
          </DialogHeader>

          {/* A refresh that failed with rows still on screen gets a banner, not
              the error pane: the list stays readable and usable, but the view
              counts and expiry in it are no longer known to be current, so
              saying nothing at all would be a silent failure. */}
          {error && shares.length > 0 && (
            <div
              role="status"
              className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            >
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span className="min-w-0 flex-1">{error}</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 shrink-0 px-2 text-xs"
                onClick={loadShares}
              >
                <RefreshCw className="mr-1 h-3 w-3" />
                {t.common.retry}
              </Button>
            </div>
          )}

          <div className="max-h-[60vh] min-h-[200px] overflow-y-auto">
            {/* Both states are gated on having nothing to show, because this
                dialog is also refreshed on tab return: a revalidation must not
                swap the shares the user is reading for a spinner, nor hand the
                whole pane to an error when the previous list is still good.
                With no shares to keep, they take the pane as before. */}
            {isLoading && shares.length === 0 ? (
              <div className="flex h-[200px] items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : error && shares.length === 0 ? (
              <div className="flex h-[200px] flex-col items-center justify-center gap-4">
                <AlertCircle className="h-8 w-8 text-destructive" />
                <p className="text-sm text-muted-foreground">{error}</p>
                <Button variant="outline" size="sm" onClick={loadShares}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  {t.common.retry}
                </Button>
              </div>
            ) : shares.length === 0 ? (
              <div className="flex h-[200px] flex-col items-center justify-center gap-2">
                <Link className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">{m.emptyTitle}</p>
                <p className="text-xs text-muted-foreground">{m.emptyHint}</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Active shares */}
                {activeShares.length > 0 && (
                  <div>
                    <h3 className="mb-3 text-sm font-medium">
                      {m.activeHeading.replace("{count}", String(activeShares.length))}
                    </h3>
                    <div className="space-y-2">
                      {activeShares.map((share) => (
                        <ShareRow
                          key={share.id}
                          share={share}
                          statusBadge={getStatusBadge(share)}
                          onCopy={() => handleCopyLink(share)}
                          onRevoke={() => setRevokeTarget(share)}
                          isCopied={copiedId === share.id}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Inactive shares */}
                {inactiveShares.length > 0 && (
                  <div>
                    <h3 className="mb-3 text-sm font-medium text-muted-foreground">
                      {m.inactiveHeading.replace("{count}", String(inactiveShares.length))}
                    </h3>
                    <div className="space-y-2">
                      {inactiveShares.map((share) => (
                        <ShareRow
                          key={share.id}
                          share={share}
                          statusBadge={getStatusBadge(share)}
                          onCopy={() => handleCopyLink(share)}
                          isCopied={copiedId === share.id}
                          disabled
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Revoke confirmation dialog */}
      <AlertDialog
        open={revokeTarget !== null}
        onOpenChange={(open) => {
          // Prevent closing while revoking
          if (!open && !isRevoking) {
            setRevokeTarget(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{m.revokeConfirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>{m.revokeConfirmDesc}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRevoking}>{t.common.cancel}</AlertDialogCancel>
            <Button
              onClick={handleRevoke}
              disabled={isRevoking}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isRevoking ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {m.revoking}
                </>
              ) : (
                m.revokeButton
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
});

// ============================================================================
// Sub-components
// ============================================================================

interface ShareRowProps {
  share: Share;
  statusBadge: React.ReactNode;
  onCopy: () => void;
  onRevoke?: () => void;
  isCopied: boolean;
  disabled?: boolean;
}

function ShareRow({ share, statusBadge, onCopy, onRevoke, isCopied, disabled }: ShareRowProps) {
  const { t, language } = useLanguage();
  const m = t.documents.manageSharesDialog;
  const isActive = !share.is_revoked && isShareActive(share);

  const expiryLabel = (() => {
    if (share.is_revoked) return m.statusRevoked;
    const info = getExpiryInfo(share.expires_at);
    if (info.status === "expired") return m.statusExpired;
    const { unit, value } = info;
    const template =
      unit === "day"
        ? value === 1
          ? m.expiry.dayLeft
          : m.expiry.daysLeft
        : unit === "hour"
          ? value === 1
            ? m.expiry.hourLeft
            : m.expiry.hoursLeft
          : value === 1
            ? m.expiry.minuteLeft
            : m.expiry.minutesLeft;
    return template.replace("{count}", String(value));
  })();

  return (
    <div
      className={`rounded-lg border p-3 ${disabled ? "bg-muted/50 opacity-60" : "bg-background"}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {/* Title and status */}
          <div className="mb-1 flex items-center gap-2">
            <span className="truncate text-sm font-medium">{share.title || m.untitledShare}</span>
            {statusBadge}
          </div>

          {/* Stats */}
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <FileText className="h-3 w-3" />
              {(share.document_count === 1 ? m.docSingular : m.docPlural).replace(
                "{count}",
                String(share.document_count)
              )}
            </span>
            <span className="flex items-center gap-1">
              <Eye className="h-3 w-3" />
              {(share.view_count === 1 ? m.viewSingular : m.viewPlural).replace(
                "{count}",
                String(share.view_count)
              )}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {expiryLabel}
            </span>
          </div>

          {/* Created info */}
          <p className="mt-1 text-xs text-muted-foreground">
            {m.createdAgo.replace(
              "{time}",
              formatDistanceToNow(new Date(share.created_at), {
                addSuffix: true,
                locale: language === "et" ? et : undefined,
              })
            )}
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onCopy}
            title={m.copyLinkTitle}
          >
            {isCopied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
          </Button>
          {isActive && onRevoke && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={onRevoke}
              title={m.revokeShareTitle}
            >
              <XCircle className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
